import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

const CategoryPageTemplate = ({
  categoryName,
  heroTitle,
  heroSubtitle,
  heroImage,
  heroMobileImage,
  subcategories
}) => {
  const singleProductSubcategories = subcategories.filter(sub => sub.products.length === 1);
  const multipleProductSubcategories = subcategories.filter(sub => sub.products.length > 1);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const pageTitle = `${categoryName} Printing Services | Kigo Prints`;
  const metaDescription = heroSubtitle || `Professional ${categoryName.toLowerCase()} printing services. High-quality prints with fast delivery. Custom designs available.`;
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  useSEO({
    title: pageTitle,
    description: metaDescription,
    keywords: `${categoryName}, printing services, custom ${categoryName.toLowerCase()}, professional printing, Kigo Prints`,
    image: heroImage,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': `${categoryName} Printing Services`,
      'description': metaDescription,
      'provider': {
        '@type': 'Organization',
        'name': 'Kigo Prints',
        'url': baseUrl
      },
      'areaServed': {
        '@type': 'Country',
        'name': 'India'
      },
      'serviceType': 'Printing Service',
      'offers': {
        '@type': 'Offer',
        'availability': 'https://schema.org/InStock'
      }
    }
  });

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white ">
      {/* Hero Section */}
      <section className="relative py-12 md:py-24 px-7 md:px-4 pt-24 md:pt-32 min-h-[350px] md:min-h-[600px] flex items-center">
        {/* Background Image - Desktop */}
        <div
          className="hidden md:block absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        {/* Background Image - Mobile */}
        <div
          className="block md:hidden absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroMobileImage || heroImage})` }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50" />
        {/* Gradient Overlay */}
        {/* <div className="absolute inset-0 bg-gradient-to-r from-[#008000]/70 to-emerald-900/50" /> */}

        <div className="max-w-7xl mx-auto relative z-10 text-center sm:text-left w-full animate-fade-in">
          <div className="max-w-4xl mx-auto sm:mx-0">
            <h1 className="text-3xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-6 leading-tight px-2 sm:px-0 animate-slide-up">
              {heroTitle}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed px-3 sm:px-0 animate-slide-up animation-delay-200">
              {heroSubtitle}
            </p>
          </div>
        </div>
      </section>


      {/* Single Product Subcategories - 2 Column Layout */}
      {singleProductSubcategories.length > 0 && (
        <section className="py-8 md:py-16 px-5 md:px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {singleProductSubcategories.map((subcategory, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02] opacity-0 animate-fade-in-up" style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}>
                  {subcategory.products.map((product) => (
                    <div key={product.id} className="flex flex-col md:flex-row md:items-stretch">
                      <Link to={`/product/${product.id}`} className="block md:w-2/5 flex-shrink-0">
                        <div className="aspect-square md:aspect-auto md:h-full overflow-hidden bg-gray-50">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                          />
                        </div>
                      </Link>
                      <div className="p-4 md:p-6 md:w-3/5 flex flex-col justify-between md:h-full">
                        <div>
                          <Link to={`/product/${product.id}`} className="block">
                            <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 hover:text-[#008000] transition-colors line-clamp-2">{product.name}</h3>
                          </Link>
                          <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 line-clamp-3 leading-relaxed">{product.description}</p>
                          {product.features && product.features.length > 0 && (
                            <ul className="text-xs md:text-sm text-gray-500 mb-4 md:mb-5 space-y-1.5">
                              {product.features.slice(0, 3).map((feature, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 flex-shrink-0 mt-1"></span>
                                  <span className="text-xs md:text-sm line-clamp-2">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <Link to={`/product/${product.id}`} className="block mt-auto">
                          <button className="w-full bg-[#008000] text-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl hover:bg-[#008000] font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                            View Details
                          </button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Multiple Product Subcategories - Grid Layout */}
      {multipleProductSubcategories.map((subcategory, index) => (
        <section key={index} className="py-8 md:py-16 px-5 md:px-4 opacity-0 animate-fade-in" style={{ animationDelay: `${(singleProductSubcategories.length * 100) + (index * 150)}ms`, animationFillMode: 'forwards' }}>
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {subcategory.products.map((product, productIndex) => (
                <div key={product.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.03] flex flex-col opacity-0 animate-fade-in-up" style={{ animationDelay: `${(singleProductSubcategories.length * 100) + (index * 150) + (productIndex * 50)}ms`, animationFillMode: 'forwards' }}>
                  <Link to={`/product/${product.id}`} className="block">
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      />
                    </div>
                  </Link>
                  <div className="p-3 md:p-6 flex flex-col justify-between flex-grow">
                    <div>
                      <Link to={`/product/${product.id}`} className="block">
                        <h3 className="text-sm md:text-xl font-semibold mb-1.5 md:mb-2 hover:text-[#008000] transition-colors line-clamp-2">{product.name}</h3>
                      </Link>
                      <p className="text-xs md:text-base text-gray-600 mb-2 md:mb-4 line-clamp-2">{product.description}</p>
                      <ul className="text-xs md:text-sm text-gray-500 mb-2.5 md:mb-4 space-y-0.5">
                        {product.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5 md:mr-2 flex-shrink-0 mt-1"></span>
                            <span className="text-xs md:text-sm line-clamp-2">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to={`/product/${product.id}`} className="block mt-auto">
                      <button className="w-full bg-[#008000] text-white px-5 md:px-8 py-2.5 md:py-4 rounded-xl hover:bg-[#008000] font-semibold text-sm md:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                        View Details
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* How to Design Section */}
      <section className="py-8 md:py-16 px-5 md:px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4 px-2">{categoryName} Design</h2>
            <p className="text-sm md:text-lg text-gray-600 px-3">
              Whether you're a beginner or a professional designer, we have {categoryName} design options for any skill level.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
            {/* Upload Design Option */}
            <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
              <div className="bg-blue-100 rounded-xl p-3 md:p-6 mb-4 md:mb-6 flex flex-col items-center">
                <div className="w-12 h-12 md:w-24 md:h-24 bg-blue-200 rounded-xl flex items-center justify-center mb-2 md:mb-4">
                  <svg className="w-8 h-8 md:w-12 md:h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="flex flex-wrap gap-1 md:gap-2 justify-center">
                  <div className="bg-red-100 text-red-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">pdf</div>
                  <div className="bg-orange-100 text-orange-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">jpg</div>
                  <div className="bg-purple-100 text-purple-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">png</div>
                  <div className="bg-yellow-100 text-yellow-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">gif</div>
                </div>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Upload a full design if you...</h3>
              <ul className="text-xs md:text-base text-gray-600 space-y-1 md:space-y-2 mb-4 md:mb-6">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Already have a complete design
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Have design chops (or your own designer)
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Are confident with your {categoryName} layout
                </li>
              </ul>
              {/* <button className="text-[#008000] font-semibold hover:text-[#008000] flex items-center text-xs md:text-base">
                Start making
                <svg className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button> */}
            </div>

            {/* Online Design Option */}
            <div className="bg-white rounded-xl px-5 p-4 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
              <div className="bg-yellow-100 rounded-xl p-3 md:p-6 mb-4 md:mb-6 flex flex-col items-center">
                <div className="w-12 h-12 md:w-24 md:h-24 bg-yellow-200 rounded-xl flex items-center justify-center mb-2 md:mb-4">
                  <svg className="w-8 h-8 md:w-12 md:h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Design your {categoryName} online if you...</h3>
              <ul className="text-xs md:text-base text-gray-600 space-y-1 md:space-y-2 mb-4 md:mb-6">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Already have your own logo
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Want to create your own {categoryName} design easily
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Want to customise every detail
                </li>
              </ul>
              {/* <button className="text-[#008000] font-semibold hover:text-[#008000] flex items-center text-xs md:text-base">
                Start making
                <svg className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button> */}
            </div>

            {/* Templates Option */}
            <div className="bg-white rounded-xl px-5 p-4 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
              <div className="bg-purple-100 rounded-xl p-3 md:p-6 mb-4 md:mb-6 flex flex-col items-center">
                <div className="w-12 h-12 md:w-24 md:h-24 bg-purple-200 rounded-xl flex items-center justify-center mb-2 md:mb-4">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="w-6 h-6 bg-white border border-gray-300 rounded"></div>
                    <div className="w-6 h-6 bg-gray-400 rounded"></div>
                    <div className="w-6 h-6 bg-gray-400 rounded"></div>
                    <div className="w-6 h-6 bg-white border border-gray-300 rounded flex items-center justify-center">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Use our templates if you...</h3>
              <ul className="text-xs md:text-base text-gray-600 space-y-1 mb-4 md:mb-6">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Are looking for inspiration
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Want a professional, quality {categoryName} design
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Want simple, fast customisation for quick card printing
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                  Are creating Small or Medium {categoryName}
                </li>
              </ul>
              {/* <button className="text-[#008000] font-semibold hover:text-[#008000] flex items-center text-xs md:text-base">
                Start making
                <svg className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* WhyKigo Print Section */}
      <section className="py-8 md:py-16 px-5 md:px-4 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4 px-2">WhyKigo Print {categoryName}?</h2>
            <p className="text-sm md:text-lg text-gray-600 px-3">
              From our products to our service, you'll feel the Kigo Prints difference at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Next Day Service */}
            <div className="text-center">
              <div className="bg-green-100 rounded-xl p-3 md:p-6 mx-auto mb-3 md:mb-6 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center relative">
                <svg className="w-8 h-8 md:w-12 md:h-12 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-[#008000] text-white text-xs px-1.5 py-0.5 md:px-2 md:py-1 rounded-full font-bold">24</div>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Next day {categoryName}!</h3>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed px-3">
                Next-day, fast {categoryName} printing is available on Original orders before 2pm (EST) Mon-Fri.
              </p>
            </div>

            {/* Printed in India */}
            <div className="text-center">
              <div className="bg-green-100 rounded-xl p-3 md:p-6 mx-auto mb-3 md:mb-6 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center relative">
                <div className="w-16 h-12 bg-gradient-to-r from-orange-500 via-white to-green-500 rounded border-2 border-gray-300 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-blue-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  </div>
                </div>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Printed in India</h3>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed px-3">
                Designed by you and Kigo Prints, printed in India with the highest quality standards.
              </p>
            </div>

            {/* Printfinity Technology */}
            <div className="text-center">
              <div className="bg-green-100 rounded-xl p-3 md:p-6 mx-auto mb-3 md:mb-6 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center relative">
                <div className="grid grid-cols-2 gap-1">
                  <div className="w-4 h-4 bg-[#008000] rounded"></div>
                  <div className="w-4 h-4 bg-green-400 rounded"></div>
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <div className="w-4 h-4 bg-green-300 rounded"></div>
                </div>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Kigo Prints makes every {categoryName} unique</h3>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed px-3">
                Print a different image or full color on the back of every single {categoryName} at no extra cost for even more creative designs.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section className="py-8 md:py-16 px-5 md:px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Frequently Asked Questions</h2>
            <p className="text-sm md:text-lg text-gray-600 px-3">
              Everything you need to know about our {categoryName} printing services
            </p>
          </div>

          <div className="space-y-3 md:space-y-6">
            {/* FAQ Item 1 */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div
                className="flex justify-between items-start cursor-pointer p-3 md:p-6"
                onClick={() => toggleFaq(0)}
              >
                <h3 className="text-sm md:text-lg font-semibold text-gray-800 flex-1 pr-2">What file formats do you accept for {categoryName}?</h3>
                <svg
                  className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${expandedFaq === 0 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedFaq === 0 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="px-3 md:px-6 pb-3 md:pb-6">
                  <p className="text-xs md:text-base text-gray-600">We accept PDF, JPG, PNG, and GIF files. For best results, please upload high-resolution files (300 DPI minimum) in CMYK color mode.</p>
                </div>
              </div>
            </div>

            {/* FAQ Item 2 */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div
                className="flex justify-between items-start cursor-pointer p-3 md:p-6"
                onClick={() => toggleFaq(1)}
              >
                <h3 className="text-sm md:text-lg font-semibold text-gray-800 flex-1 pr-2">How long does it take to print my {categoryName}?</h3>
                <svg
                  className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${expandedFaq === 1 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedFaq === 1 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="px-3 md:px-6 pb-3 md:pb-6">
                  <p className="text-xs md:text-base text-gray-600">Standard orders take 3-5 business days. Next-day printing is available for Original paper orders placed before 2pm EST, Monday through Friday.</p>
                </div>
              </div>
            </div>

            {/* FAQ Item 3 */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div
                className="flex justify-between items-start cursor-pointer p-3 md:p-6"
                onClick={() => toggleFaq(2)}
              >
                <h3 className="text-sm md:text-lg font-semibold text-gray-800 flex-1 pr-2">What paper options are available?</h3>
                <svg
                  className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${expandedFaq === 2 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedFaq === 2 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="px-3 md:px-6 pb-3 md:pb-6">
                  <p className="text-xs md:text-base text-gray-600">We offer Original (350 GSM), Premium (400 GSM), and Glossy finish options. Each provides different textures and finishes to suit your needs.</p>
                </div>
              </div>
            </div>

            {/* FAQ Item 4 */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div
                className="flex justify-between items-start cursor-pointer p-3 md:p-6"
                onClick={() => toggleFaq(3)}
              >
                <h3 className="text-sm md:text-lg font-semibold text-gray-800 flex-1 pr-2">Do you offer bulk discounts?</h3>
                <svg
                  className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${expandedFaq === 3 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedFaq === 3 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="px-3 md:px-6 pb-3 md:pb-6">
                  <p className="text-xs md:text-base text-gray-600">Yes! The more you order, the more you save. Our pricing automatically adjusts based on quantity, with significant savings on larger orders.</p>
                </div>
              </div>
            </div>

            {/* FAQ Item 5 */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div
                className="flex justify-between items-start cursor-pointer p-3 md:p-6"
                onClick={() => toggleFaq(4)}
              >
                <h3 className="text-sm md:text-lg font-semibold text-gray-800 flex-1 pr-2">Can I get a proof before printing?</h3>
                <svg
                  className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${expandedFaq === 4 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedFaq === 4 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="px-3 md:px-6 pb-3 md:pb-6">
                  <p className="text-xs md:text-base text-gray-600">Absolutely! We provide digital proofs for all orders. You can review and approve your design before we proceed with printing to ensure everything looks perfect.</p>
                </div>
              </div>
            </div>

            {/* FAQ Item 6 */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div
                className="flex justify-between items-start cursor-pointer p-3 md:p-6"
                onClick={() => toggleFaq(5)}
              >
                <h3 className="text-sm md:text-lg font-semibold text-gray-800 flex-1 pr-2">What if I'm not satisfied with my order?</h3>
                <svg
                  className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${expandedFaq === 5 ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedFaq === 5 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="px-3 md:px-6 pb-3 md:pb-6">
                  <p className="text-xs md:text-base text-gray-600">We stand behind our quality. If you're not completely satisfied with your {categoryName}, contact our customer service team and we'll make it right.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-8 md:py-16 px-5 md:px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 md:mb-12">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-1 md:gap-2 mb-3 md:mb-4">
              <div className="flex text-green-500">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 md:w-6 md:h-6 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <span className="text-lg md:text-xl font-bold text-gray-800">4.9</span>
              <span className="text-sm md:text-base text-gray-600">/ 5</span>
              <span className="text-sm md:text-base text-gray-600">394 reviews</span>
              {/* <img src="https://via.placeholder.com/100x30/10b981/ffffff?text=Trustpilot" alt="Trustpilot" className="h-4 md:h-6" /> */}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {/* Review 1 */}
            <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg">
              <div className="flex text-green-500 mb-2 md:mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-3 h-3 md:w-4 md:h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p className="text-xs md:text-base text-gray-700 mb-2 md:mb-4">"Beautiful quality prints, loved the texture of the cardstock and that I could add so many custom details to my {categoryName}."</p>
              <div className="text-xs md:text-sm">
                <p className="font-semibold text-gray-800">Dhakshayini Boopalan</p>
                <p className="text-gray-500">4 days ago</p>
              </div>
            </div>

            {/* Review 2 */}
            <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg">
              <div className="flex text-green-500 mb-2 md:mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-3 h-3 md:w-4 md:h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p className="text-xs md:text-base text-gray-700 mb-2 md:mb-4">"Fast delivery and excellent quality. The {categoryName} exceeded my expectations and the customer service was outstanding."</p>
              <div className="text-xs md:text-sm">
                <p className="font-semibold text-gray-800">Rajesh Kumar</p>
                <p className="text-gray-500">1 week ago</p>
              </div>
            </div>

            {/* Review 3 */}
            <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg">
              <div className="flex text-green-500 mb-2 md:mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-3 h-3 md:w-4 md:h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p className="text-xs md:text-base text-gray-700 mb-2 md:mb-4">"Professional results at affordable prices. Will definitely order again for all my printing needs."</p>
              <div className="text-xs md:text-sm">
                <p className="font-semibold text-gray-800">Priya Sharma</p>
                <p className="text-gray-500">2 weeks ago</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Even More Good Stuff Section */}


      {/* Free Delivery Banner */}
      <section className="bg-gradient-to-r from-green-50 to-emerald-50 py-4 md:py-6 px-3 md:px-4 border-t border-b border-green-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-2 md:gap-3">
            <svg className="w-5 h-5 md:w-6 md:h-6 text-[#008000] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <p className="text-sm md:text-base font-semibold text-[#008000]">
              Free delivery on orders above Rs.500
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-100 py-8 md:py-16 px-3 md:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl md:text-3xl font-bold mb-2 md:mb-4 px-2">Ready to get started?</h2>
          <p className="text-sm md:text-lg text-gray-600 mb-4 md:mb-8 px-3">
            Contact us today for custom printing solutions tailored to your needs.
          </p>
          <Link to="mailto:xpressprint.in">
            <button className="bg-[#008000] text-white px-5 md:px-8 py-2.5 md:py-3 rounded-xl hover:bg-[#008000] text-sm md:text-lg font-medium">
              Contact Us
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CategoryPageTemplate;
